/**
 * BuriedVerse e03_prodigal "Prodigal" — FULL overlay composition.
 * Generated by bv_overlay_gen.py. fps-SAFE (S(sec) reads useVideoConfig().fps) so the SAME comp is:
 *   • buriedverse-e03-overlays      @16fps → the 720p 0.5x preview (baked logo)
 *   • buriedverse-e03-overlays-4k   @24fps → the Topaz 4K base, logo via ffmpeg (showLogo=false)
 * Devotional route (== bible_decoder W01): overlay_design_spec route=devotional; build_overlay_windows.py
 * EXTRACTS every `start: X, out: Y` NUMERIC LITERAL below → factory_4k_full renders those clusters @scale=3.
 * Anchors are REAL times from productions.word_timestamps_hindi. 23 events (3 BvFactTag + 20 BvHero).
 */
import {
  AbsoluteFill, Img, Sequence, staticFile, useCurrentFrame, useVideoConfig, interpolate,
} from "remotion";
import { BvFactTag, BvHero } from "./BuriedVerseOverlayDemo";

export const e03_prodigal_FPS = 16;
export const e03_prodigal_DURATION_FRAMES = 25279;
export const e03_prodigal_FPS_4K = 24;
export const e03_prodigal_DURATION_FRAMES_4K = 37919;

const BvLogo: React.FC = () => {
  const frame = useCurrentFrame();
  const fadeIn = interpolate(frame, [0, 36], [0, 1], { extrapolateLeft: "clamp", extrapolateRight: "clamp" });
  return (
    <Img src={staticFile("buried_verse_diorama_ko.png")}
      style={{ position: "absolute", top: 20, right: 20, width: 70, height: "auto", opacity: fadeIn * 0.85 }} />
  );
};

const FadeOut: React.FC<{ dur: number; children: React.ReactNode }> = ({ dur, children }) => {
  const frame = useCurrentFrame();
  const o = interpolate(frame, [dur - 14, dur], [1, 0], { extrapolateLeft: "clamp", extrapolateRight: "clamp" });
  return <AbsoluteFill style={{ opacity: o }}>{children}</AbsoluteFill>;
};

type Tag = { start: number; out: number; kicker: string; value: string };
type Hero = {
  start: number; out: number; variant: "word" | "myth" | "quote"; kicker: string;
  word?: string; translit?: string; gloss?: string; headline?: string; attribution?: string;
};

// ── SLOT 1 · fact tags (BvFactTag: citation / place / author) ──
const TAGS: Tag[] = [
  // it changes everything. Luke chapter fifteen opens with a scene.
  { start: 105.757, out: 110.357, kicker: "Citation", value: "Luke 15" },
  // Deuteronomy there is a law for the stubborn and rebellious son, the one who
  { start: 302.265, out: 306.865, kicker: "Citation", value: "Deuteronomy 21 · the rebellious son" },
  // more. Some scholars, most notably Kenneth Bailey, who spent decades
  { start: 825.082, out: 829.682, kicker: "Author", value: "Kenneth Bailey · honor-shame reading" },
];

// ── SLOT 2 · hero cards (BvHero: word / myth / quote) ──
const HEROES: Hero[] = [
  // an editorial heading; Jesus never gave this story a name at all, and
  { start: 24.956, out: 31.956, variant: "myth", kicker: "The myth", headline: "Jesus never named this story. “The Prodigal Son” is an editor’s heading." },
  // lost, or rebellious. It comes from a Latin word, prodigus, and it means
  { start: 42.467, out: 49.667, variant: "word", kicker: "The word", word: "prodigus", translit: "prōdigus · Latin", gloss: "Recklessly wasteful. Lavish to the point of folly." },
  // to see that the most extravagant person in the parable of the prodigal
  { start: 75.985, out: 82.985, variant: "myth", kicker: "The reveal", headline: "The most extravagant spender in the parable is not the son." },
  // short, poisonous sentence: "This man welcomes sinners and eats with them." Tha
  { start: 126.062, out: 133.262, variant: "quote", kicker: "The trigger", headline: "“This man welcomes sinners and eats with them.”", attribution: "Luke 15:2" },
  // Notice the escalation. Sheep, coin, son. And look at
  { start: 164.426, out: 171.426, variant: "myth", kicker: "The pattern", headline: "Sheep. Coin. Son. The first two teach you how to read the third." },
  // out loud that you wished he were already dead, that you were
  { start: 274.613, out: 281.613, variant: "myth", kicker: "The demand", headline: "“Give me my share” meant “I wish you were already dead.”" },
  // living between them." The Greek word there is bios. In this context
  { start: 383.53, out: 390.73, variant: "word", kicker: "The word", word: "βίος", translit: "bios", gloss: "His livelihood. Also the ordinary Greek word for LIFE." },
  // Israel who had made himself lower than a pig. That is the
  { start: 626.365, out: 633.365, variant: "myth", kicker: "The pit", headline: "Lower than the pigs. The unclean animals had a keeper who fed them. He had no one." },
  // The text says, "when he came to himself." Not "when he
  { start: 643.99, out: 651.19, variant: "quote", kicker: "The phrase", headline: "“When he came to himself.” As if he had walked back into his own mind.", attribution: "Luke 15:17" },
  // he wants to become is misthios, a hired worker, a man paid
  { start: 692.746, out: 699.946, variant: "word", kicker: "The word", word: "μίσθιος", translit: "misthios", gloss: "A hired hand, paid by the day, not family at all." },
  // says he was filled with compassion, and he ran. That single word,
  { start: 781.082, out: 788.082, variant: "myth", kicker: "The father runs", headline: "He saw him a long way off and ran. Robes hitched, dignity spent in the dust." },
  // a hired servant. He gets the first two out. He never gets to the third. Now, o
  { start: 942.0, out: 949.0, variant: "myth", kicker: "The detail", headline: "He rehearsed three lines. The father cuts him off before the third, before “hired servant.”" },
  // the son's plan. Bring the best robe, he says, and
  { start: 982.08, out: 989.08, variant: "myth", kicker: "The restoration", headline: "The best robe, a signet ring, sandals, the fattened calf. Every order undoes the son’s plan." },
  // he was doing. The son was prodigal by accident; the father was
  { start: 1134.961, out: 1141.961, variant: "myth", kicker: "The reveal", headline: "The son was prodigal by accident. The father was prodigal on purpose." },
  // road, and now he leaves the party to go out into the dark to the older son, pl
  { start: 1198.666, out: 1205.666, variant: "myth", kicker: "Twice", headline: "The father goes out twice: down the road to the son who ran, out into the dark to the son who stayed." },
  // is making. "Look, these many years I have served you, and I never
  { start: 1214.317, out: 1221.317, variant: "myth", kicker: "The confession", headline: "“All these years I served you.” He felt like a slave in a house that was already his." },
  // the parable: "Son, you are always with me, and all that is mine is yours. It w
  { start: 1301.006, out: 1308.206, variant: "quote", kicker: "The text", headline: "“Son, you are always with me, and all that is mine is yours.”", attribution: "Luke 15:31" },
  // story. And then the story simply stops. Read it again
  { start: 1353.458, out: 1360.458, variant: "myth", kicker: "The turn", headline: "Jesus refused to give it an ending. He hands you the pen at the open door." },
  // stranger. He is the most religious man in the story, and he is
  { start: 1516.106, out: 1523.106, variant: "myth", kicker: "The target", headline: "The older brother is the most religious man in the story, and the one left outside." },
  // thousand years ago: which brother are you, and are you going to go in.
  { start: 1569.488, out: 1576.488, variant: "myth", kicker: "The question", headline: "Which brother are you, and are you going to go in?" },
];

export const BuriedVerseE03ProdigalOverlays: React.FC<{ showLogo?: boolean }> = ({ showLogo = true }) => {
  const { fps } = useVideoConfig();
  const S = (sec: number) => Math.round(sec * fps);
  return (
    <AbsoluteFill>
      {showLogo && <BvLogo />}
      {TAGS.map((t, i) => (
        <Sequence key={`tag-${i}`} from={S(t.start)} durationInFrames={S(t.out - t.start)}>
          <FadeOut dur={S(t.out - t.start)}>
            <BvFactTag kicker={t.kicker} value={t.value} />
          </FadeOut>
        </Sequence>
      ))}
      {HEROES.map((h, i) => (
        <Sequence key={`hero-${i}`} from={S(h.start)} durationInFrames={S(h.out - h.start)}>
          <FadeOut dur={S(h.out - h.start)}>
            <BvHero variant={h.variant} kicker={h.kicker} word={h.word} translit={h.translit}
              gloss={h.gloss} headline={h.headline} attribution={h.attribution} />
          </FadeOut>
        </Sequence>
      ))}
    </AbsoluteFill>
  );
};

;
    var _remotion_globalVariableA;
    var _remotion_globalVariableB;
    // Legacy CSS implementations will `eval` browser code in a Node.js context
    // to extract CSS. For backwards compatibility, we need to check we're in a
    // browser context before continuing.
    if (typeof self !== 'undefined' &&
        // AMP / No-JS mode does not inject these helpers:
        '$RefreshHelpers$' in self) {
        const currentExports = __webpack_module__.exports;
        const prevExports = (_remotion_globalVariableB = (_remotion_globalVariableA = __webpack_module__.hot.data) === null || _remotion_globalVariableA === void 0 ? void 0 : _remotion_globalVariableA.prevExports) !== null && _remotion_globalVariableB !== void 0 ? _remotion_globalVariableB : null;
        // This cannot happen in MainTemplate because the exports mismatch between
        // templating and execution.
        self.$RefreshHelpers$.registerExportsForReactRefresh(currentExports, __webpack_module__.id);
        // A module can be accepted automatically based on its exports, e.g. when
        // it is a Refresh Boundary.
        if (self.$RefreshHelpers$.isReactRefreshBoundary(currentExports)) {
            // Save the previous exports on update so we can compare the boundary
            // signatures.
            __webpack_module__.hot.dispose((data) => {
                data.prevExports = currentExports;
            });
            // Unconditionally accept an update to this module, we'll check if it's
            // still a Refresh Boundary later.
            __webpack_module__.hot.accept();
            // This field is set when the previous version of this module was a
            // Refresh Boundary, letting us know we need to check for invalidation or
            // enqueue an update.
            if (prevExports !== null) {
                // A boundary can become ineligible if its exports are incompatible
                // with the previous exports.
                //
                // For example, if you add/remove/change exports, we'll want to
                // re-execute the importing modules, and force those components to
                // re-render. Similarly, if you convert a class component to a
                // function, we want to invalidate the boundary.
                if (self.$RefreshHelpers$.shouldInvalidateReactRefreshBoundary(prevExports, currentExports)) {
                    __webpack_module__.hot.invalidate();
                }
                else {
                    self.$RefreshHelpers$.scheduleUpdate();
                }
            }
        }
        else {
            // Since we just executed the code for the module, it's possible that the
            // new exports made it ineligible for being a boundary.
            // We only care about the case when we were _previously_ a boundary,
            // because we already accepted this update (accidental side effect).
            const isNoLongerABoundary = prevExports !== null;
            if (isNoLongerABoundary) {
                __webpack_module__.hot.invalidate();
            }
        }
    }
